import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { getClassMemberDisplayNumbers } from '../utils/classMemberNumbers';
import { createPortal } from 'react-dom';
import AutoStatusToast from '../components/common/AutoStatusToast';
import BasicSettingIcon from '../components/common/BasicSettingIcon';
import { AssignmentBlockCard } from '../components/assignmentPoints/AssignmentBlockCard';
import AssignmentKindEmptyState from '../components/assignmentPoints/AssignmentKindEmptyState';
import AssignmentCheckPanel, {
  type CheckPanelSelection,
} from '../components/assignmentPoints/AssignmentCheckPanel';
import AssignmentDateInputModal from '../components/assignmentPoints/AssignmentDateInputModal';
import AssignmentIncompleteReportModal, {
  type IncompleteReportMode,
} from '../components/assignmentPoints/AssignmentIncompleteReportModal';
import AssignmentManagementTableModal from '../components/assignmentPoints/AssignmentManagementTableModal';
import AssignmentPointsSettingsModal from '../components/assignmentPoints/AssignmentPointsSettingsModal';
import GeneralAssignmentSettingsModal, {
  type FlatMember,
} from '../components/assignmentPoints/GeneralAssignmentSettingsModal';
import NoteGroupSelector from '../components/assignmentPoints/NoteGroupSelector';
import { usePaidPassGate } from '../contexts/PaidPassGateContext';
import ProjectAssignmentSettingsModal from '../components/assignmentPoints/ProjectAssignmentSettingsModal';
import AssignmentRewardResultModal from '../components/assignmentPoints/AssignmentRewardResultModal';
import ProjectMemberReachModal from '../components/assignmentPoints/ProjectMemberReachModal';
import RoutineAssignmentSettingsModal from '../components/assignmentPoints/RoutineAssignmentSettingsModal';
import RoutineMemberReachModal from '../components/assignmentPoints/RoutineMemberReachModal';
import ChallengeEditorModal from '../components/assignmentChallenge/ChallengeEditorModal';
import ChallengeShareModal from '../components/assignmentChallenge/ChallengeShareModal';
import {
  createChallengeProject,
  deleteChallengeProjects,
  fetchChallengeBundle,
  duplicateChallengeItems,
  fetchChallengeSubmissionSummaries,
  formatChallengeError,
  syncChallengeItemsFromSubTasks,
  updateChallengeProjectMeta,
  type ChallengeSubmissionSummary,
} from '../services/assignmentChallengeService';
import {
  defaultChallengeContent,
  normalizeChallengeIntro,
  normalizeChallengeThemeColor,
} from '../types/assignmentChallenge';
import { pruneProjectStudentStatuses } from '../utils/assignmentEditCleanup';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import {
  getAssignmentPointsNote,
  saveAssignmentPointsNote,
  type SaveAssignmentPointsNoteOpts,
} from '../services/assignmentPointsNoteService';
import {
  setLiveAssignmentPointsNoteDoc,
  shouldApplyRemoteAssignmentPointsNote,
  subscribeAssignmentPointsNoteDoc,
} from '../utils/assignmentPointsNoteTabBridge';
import {
  loadNoteSelectedGroupId,
  resolveNoteSelectedGroupId,
  saveNoteSelectedGroupId,
} from '../utils/noteSelectedGroupPreference';
import { subscribeSelectedClassGroupChanged } from '../utils/selectedClassGroupPreference';
import { getClassMemberGradeLayoutsMap } from '../services/classMembersService';
import {
  getPointItemNoteSettings,
  loadGroupPointsForAssignmentReward,
  saveGroupAssignmentRewardMutations,
} from '../services/pointItemNoteService';
import { emptyPointItemNoteDoc, type ItemCard } from '../types/pointItemNote';
import type {
  AssignmentCoverImage,
  AssignmentPointsItem,
  AssignmentPointsKind,
  AssignmentPointsNoteDoc,
  AssignmentPointsSettings,
  AssignmentStudentStatus,
  GeneralAssignmentItem,
  ProjectAssignmentItem,
  RoutineAssignmentItem,
} from '../types/assignmentPointsNote';
import {
  ASSIGNMENT_COVER_IMAGE_MAX,
  emptyAssignmentPointsNoteDoc,
  isChallengeAssignment,
} from '../types/assignmentPointsNote';
import { preloadAssignmentCoverImages } from '../utils/assignmentCoverImageCache';
import {
  calculateRoutineDates,
  countProjectPending,
  countRoutinePendingTotal,
  ensureRoutineDateBuckets,
  isGeneralComplete,
  listAllRoutineDates,
  mergeScopedStudentStatuses,
} from '../utils/assignmentPointsNoteProgress';
import {
  buildGeneralAssignmentsExportHtml,
  buildPrintStudents,
  buildProjectAssignmentsExportHtml,
  formatAssignmentDueDateFromIso,
  openAssignmentPrintWindow,
} from '../utils/assignmentPrintUtils';
import {
  assignmentRewardNeedsPointDocCheck,
  processProjectAssignmentRewardsInMemory,
  processRoutineAssignmentRewardsInMemory,
  reconcileGrantedAssignmentRewardsInMemory,
  type AssignmentRewardProcessResult,
} from '../utils/assignmentRewardSync';
import {
  emptyGradeLayout,
  type ClassMemberGroupMeta,
} from '../utils/classMembersStorage';

type ListFilterType = 'all' | 'incomplete';
type ListSortBy = 'createdAt' | 'dueDate' | 'title';
/** 'challenge'는 저장 시 kind 'project' + challenge 연결로 구분 */
type CreateKind = AssignmentPointsKind | 'challenge';

/** 과제 보드 유형 탭 — 'all'이면 모든 종류를 한 보드에 함께 표시 */
type AssignmentBoardKind = 'all' | CreateKind;

const ASSIGNMENT_BOARD_KINDS: { kind: AssignmentBoardKind; label: string }[] = [
  { kind: 'all', label: '전체' },
  { kind: 'general', label: '일반' },
  { kind: 'project', label: '프로젝트' },
  { kind: 'routine', label: '루틴' },
  { kind: 'challenge', label: '챌린지' },
];

const ASSIGNMENT_CREATE_KINDS: { kind: CreateKind; label: string; title: string }[] = [
  { kind: 'general', label: '일반', title: '일반 과제' },
  { kind: 'project', label: '프로젝트', title: '프로젝트 과제' },
  { kind: 'routine', label: '루틴', title: '루틴 과제' },
  { kind: 'challenge', label: '챌린지', title: '챌린지 과제' },
];

function boardKindOf(item: AssignmentPointsItem): CreateKind {
  return isChallengeAssignment(item) ? 'challenge' : item.kind;
}

type KindListPrefs = {
  filterType: ListFilterType;
  sortBy: ListSortBy;
  categoryId: string;
};

const DEFAULT_KIND_LIST_PREFS: KindListPrefs = {
  filterType: 'all',
  sortBy: 'createdAt',
  categoryId: '',
};

function isItemIncomplete(
  item: AssignmentPointsItem,
  memberIdSet?: Set<string> | null
): boolean {
  if (item.kind === 'general') return !isGeneralComplete(item, memberIdSet);
  if (item.kind === 'project') return countProjectPending(item, memberIdSet) > 0;
  return countRoutinePendingTotal(ensureRoutineDateBuckets(item), memberIdSet) > 0;
}

function sortLabel(sortBy: ListSortBy): string {
  if (sortBy === 'dueDate') return '마감일';
  if (sortBy === 'title') return '제목';
  return '생성일';
}

function kindFilterSummary(
  prefs: KindListPrefs,
  kind: AssignmentBoardKind,
  categoryById: Map<string, { name: string }>
): string {
  const status = prefs.filterType === 'all' ? '전체 과제' : '미완료 과제';
  const sort = sortLabel(prefs.sortBy);
  if (kind !== 'general') return `${status} · ${sort}`;
  const cat =
    prefs.categoryId === ''
      ? '전체 카테고리'
      : categoryById.get(prefs.categoryId)?.name || '카테고리';
  return `${status} · ${sort} · ${cat}`;
}

function filterSortKindItems(
  items: AssignmentPointsItem[],
  kind: AssignmentBoardKind,
  prefs: KindListPrefs,
  memberIdSet?: Set<string> | null
): AssignmentPointsItem[] {
  let list = kind === 'all' ? items.slice() : items.filter((i) => boardKindOf(i) === kind);
  if (prefs.filterType === 'incomplete') {
    list = list.filter((i) => isItemIncomplete(i, memberIdSet));
  }
  if (kind === 'general' && prefs.categoryId) {
    list = list.filter(
      (i) => i.kind === 'general' && (i.categoryId ?? '') === prefs.categoryId
    );
  }
  return list.slice().sort((a, b) => {
    if (prefs.sortBy === 'title') {
      return (a.title || '').localeCompare(b.title || '', 'ko');
    }
    if (prefs.sortBy === 'dueDate') {
      const cmp = (a.dueDate || '').localeCompare(b.dueDate || '');
      if (cmp !== 0) return cmp;
    } else {
      const cmp = (b.createdAt || '').localeCompare(a.createdAt || '');
      if (cmp !== 0) return cmp;
    }
    return (b.createdAt || '').localeCompare(a.createdAt || '');
  });
}

function flattenMembers(
  layout: ReturnType<typeof emptyGradeLayout>
): FlatMember[] {
  const groups = [...layout.groups].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)
  );
  const out: FlatMember[] = [];
  for (const g of groups) {
    const rows = layout.membersByGroupId[g.id] ?? [];
    const nums = getClassMemberDisplayNumbers(rows);
    rows.forEach((m, i) => {
      out.push({
        ...m,
        orderIndex: nums[i] ?? i + 1,
        groupId: g.id,
        groupName: g.name?.trim() || '그룹',
      });
    });
  }
  return out;
}

function snapshotDoc(doc: AssignmentPointsNoteDoc): string {
  return JSON.stringify({
    settings: doc.settings,
    items: doc.items,
    coverImages: doc.coverImages ?? [],
  });
}

type AssignmentBlockLayoutMode = 'horizontal' | 'vertical';

const ASSIGNMENT_LAYOUT_MODE_KEY = 'cp.assignmentPoints.blockLayoutMode';
const ASSIGNMENT_BOARD_KIND_KEY = 'cp.assignmentPoints.boardKind';

function loadAssignmentBoardKind(): AssignmentBoardKind {
  if (typeof window === 'undefined') return 'all';
  try {
    const v = window.localStorage.getItem(ASSIGNMENT_BOARD_KIND_KEY);
    return v === 'general' || v === 'project' || v === 'routine' || v === 'challenge' ? v : 'all';
  } catch {
    return 'all';
  }
}

function saveAssignmentBoardKind(kind: AssignmentBoardKind): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ASSIGNMENT_BOARD_KIND_KEY, kind);
  } catch {
    /* ignore */
  }
}

function loadAssignmentBlockLayoutMode(): AssignmentBlockLayoutMode {
  if (typeof window === 'undefined') return 'horizontal';
  try {
    return window.localStorage.getItem(ASSIGNMENT_LAYOUT_MODE_KEY) === 'vertical'
      ? 'vertical'
      : 'horizontal';
  } catch {
    return 'horizontal';
  }
}

function saveAssignmentBlockLayoutMode(mode: AssignmentBlockLayoutMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ASSIGNMENT_LAYOUT_MODE_KEY, mode);
  } catch {
    /* ignore */
  }
}

const AssignmentPointsNotePage: React.FC<{
  workspaceId: string;
  currentGradeId?: string;
}> = ({ workspaceId, currentGradeId = '' }) => {
  const { setAssignmentPointsNoteDirty, saveAssignmentPointsNoteRef } =
    useContext(GradeTrackLeaveContext);
  const { toastState: saveToastState, showSaving, showSuccess, showError } = useAutoStatusToast(3000);
  /** 이용권별 권한: 프로젝트·루틴·챌린지 과제 만들기(assignment_advanced) */
  const { requireFeature, isFeatureLocked } = usePaidPassGate();
  const advancedAssignmentLocked = isFeatureLocked('assignment_advanced');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [blockLayoutMode, setBlockLayoutMode] = useState<AssignmentBlockLayoutMode>(
    loadAssignmentBlockLayoutMode
  );
  const [boardKind, setBoardKind] = useState<AssignmentBoardKind>(loadAssignmentBoardKind);
  const [doc, setDoc] = useState<AssignmentPointsNoteDoc>(() =>
    emptyAssignmentPointsNoteDoc(workspaceId, currentGradeId)
  );
  const [allMembers, setAllMembers] = useState<FlatMember[]>([]);
  const [groups, setGroups] = useState<ClassMemberGroupMeta[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    return subscribeSelectedClassGroupChanged((detail) => {
      if (detail.workspaceId !== workspaceId.trim()) return;
      if (detail.gradeId !== currentGradeId.trim()) return;
      const ids = new Set(groups.map((g) => g.id));
      if (detail.groupId && ids.has(detail.groupId)) {
        setSelectedGroupId(detail.groupId);
      }
    });
  }, [workspaceId, currentGradeId, groups]);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [projectSubTaskIndex, setProjectSubTaskIndex] = useState(0);
  const [routineFocusDate, setRoutineFocusDate] = useState<string | null>(null);
  /** 과제 체크 모달 — 일반 블럭 클릭 / 프로젝트·루틴 하위항목 선택 시에만 true */
  const [assignmentCheckOpen, setAssignmentCheckOpen] = useState(false);
  const [checkPanelDirty, setCheckPanelDirty] = useState(false);
  const [checkCloseConfirmOpen, setCheckCloseConfirmOpen] = useState(false);
  const [checkClosing, setCheckClosing] = useState(false);
  const checkPanelDirtyRef = useRef(false);
  const checkSaveActionsRef = useRef<{ save: () => Promise<void> } | null>(null);

  const handleCheckPanelDirtyChange = useCallback((dirty: boolean) => {
    checkPanelDirtyRef.current = dirty;
    setCheckPanelDirty(dirty);
  }, []);
  const [reachModalOpen, setReachModalOpen] = useState(false);
  const [dateInputOpen, setDateInputOpen] = useState(false);

  const [generalModal, setGeneralModal] = useState<{
    open: boolean;
    mode: 'new' | 'edit' | 'copy';
    item?: GeneralAssignmentItem | null;
  }>({ open: false, mode: 'new' });
  const [projectModal, setProjectModal] = useState<{
    open: boolean;
    mode: 'new' | 'edit' | 'copy';
    item?: ProjectAssignmentItem | null;
    challenge?: boolean;
  }>({ open: false, mode: 'new' });
  const [challengeEditorItemId, setChallengeEditorItemId] = useState<string | null>(null);
  const [challengeShareItemId, setChallengeShareItemId] = useState<string | null>(null);
  const [challengeSummaries, setChallengeSummaries] = useState<ChallengeSubmissionSummary[]>([]);
  /** projectId → 하위 과제 id → 카드 표시 정보(썸네일 등) */
  const [challengeCardInfo, setChallengeCardInfo] = useState<
    Record<string, Record<string, { thumbnailUrl: string | null; kicker: string; isVisible: boolean; missionCount: number }>>
  >({});
  const [routineModal, setRoutineModal] = useState<{
    open: boolean;
    mode: 'new' | 'edit' | 'copy';
    item?: RoutineAssignmentItem | null;
  }>({ open: false, mode: 'new' });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [incompleteReportMode, setIncompleteReportMode] =
    useState<IncompleteReportMode | null>(null);
  const [assignmentRewardItems, setAssignmentRewardItems] = useState<ItemCard[]>([]);
  const [kindListPrefs, setKindListPrefs] = useState<
    Record<AssignmentBoardKind, KindListPrefs>
  >({
    all: { ...DEFAULT_KIND_LIST_PREFS },
    general: { ...DEFAULT_KIND_LIST_PREFS },
    project: { ...DEFAULT_KIND_LIST_PREFS },
    routine: { ...DEFAULT_KIND_LIST_PREFS },
    challenge: { ...DEFAULT_KIND_LIST_PREFS },
  });
  const [filterMenuKind, setFilterMenuKind] = useState<AssignmentBoardKind | null>(null);
  const [printSelectionKinds, setPrintSelectionKinds] = useState<
    Set<AssignmentPointsKind>
  >(() => new Set());
  const [printSelectedIds, setPrintSelectedIds] = useState<Set<string>>(() => new Set());
  const [isExportingPrint, setIsExportingPrint] = useState(false);
  const [rewardProcessingId, setRewardProcessingId] = useState<string | null>(null);
  const [rewardResult, setRewardResult] = useState<AssignmentRewardProcessResult | null>(null);
  const [rewardResultTitle, setRewardResultTitle] = useState('과제 보상 처리 결과');

  const baselineRef = useRef('');
  const docRef = useRef(doc);
  docRef.current = doc;

  const gradeReady = Boolean(workspaceId && currentGradeId);

  const load = useCallback(async () => {
    if (!gradeReady) {
      setDoc(emptyAssignmentPointsNoteDoc(workspaceId, currentGradeId));
      setAllMembers([]);
      setGroups([]);
      setSelectedGroupId(null);
      setAssignmentRewardItems([]);
      baselineRef.current = '';
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const [nextDoc, layouts, pinSettings] = await Promise.all([
        getAssignmentPointsNote(workspaceId, currentGradeId),
        getClassMemberGradeLayoutsMap(workspaceId, [currentGradeId]),
        // 아이템 카드 설정만 — 멤버 코인/아이템/포인트 테이블 전부 로드하지 않음
        getPointItemNoteSettings(workspaceId, currentGradeId),
      ]);
      const layout = layouts[currentGradeId] ?? emptyGradeLayout();
      const nextGroups = [...layout.groups].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)
      );
      setDoc(nextDoc);
      setAssignmentRewardItems(pinSettings.itemCards.assignmentItem ?? []);
      setAllMembers(flattenMembers(layout));
      setGroups(nextGroups);
      setSelectedGroupId((prev) => {
        const next = resolveNoteSelectedGroupId(
          nextGroups.map((g) => g.id),
          [prev, loadNoteSelectedGroupId('assignmentPoints', workspaceId, currentGradeId)]
        );
        if (next) {
          saveNoteSelectedGroupId('assignmentPoints', workspaceId, currentGradeId, next);
        }
        return next;
      });
      baselineRef.current = snapshotDoc(nextDoc);
      setSelectedId(null);
      setExpandedId(null);
      setAssignmentCheckOpen(false);
      setPrintSelectionKinds(new Set());
      setPrintSelectedIds(new Set());
      setLiveAssignmentPointsNoteDoc(nextDoc);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : '불러오기에 실패했습니다.');
      setDoc(emptyAssignmentPointsNoteDoc(workspaceId, currentGradeId));
      setAllMembers([]);
      setGroups([]);
      setSelectedGroupId(null);
      setAssignmentRewardItems([]);
      baselineRef.current = '';
    } finally {
      setLoading(false);
    }
  }, [workspaceId, currentGradeId, gradeReady]);

  useEffect(() => {
    void load();
  }, [load]);

  const isDirty = gradeReady && baselineRef.current !== '' && snapshotDoc(doc) !== baselineRef.current;
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    if (!gradeReady) return undefined;
    return subscribeAssignmentPointsNoteDoc((remote) => {
      if (remote.workspaceId !== workspaceId || remote.gradeId !== currentGradeId) return;
      if (isDirtyRef.current) return;
      setDoc((local) => {
        if (
          !shouldApplyRemoteAssignmentPointsNote({
            remote,
            local,
            isDirty: false,
          })
        ) {
          return local;
        }
        baselineRef.current = snapshotDoc(remote);
        setLiveAssignmentPointsNoteDoc(remote);
        return remote;
      });
    });
  }, [gradeReady, workspaceId, currentGradeId]);

  useEffect(() => {
    setAssignmentPointsNoteDirty(isDirty);
  }, [isDirty, setAssignmentPointsNoteDirty]);

  const memberGroupMap = useMemo(() => {
    const map = new Map<string, string>();
    allMembers.forEach((m) => {
      if (m.groupId) map.set(m.id, m.groupId);
    });
    return map;
  }, [allMembers]);

  /** 배정 멤버가 2개 이상 그룹에 걸친 과제 ID */
  const sharedAssignmentIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of doc.items) {
      const groupIds = new Set<string>();
      for (const memberId of item.assignedMemberIds) {
        const gid = memberGroupMap.get(memberId);
        if (!gid) continue;
        groupIds.add(gid);
        if (groupIds.size >= 2) {
          ids.add(item.id);
          break;
        }
      }
    }
    return ids;
  }, [doc.items, memberGroupMap]);

  /** 로컬 상태 갱신 후 즉시 DB 반영 */
  const commitDoc = useCallback(
    async (
      next: AssignmentPointsNoteDoc,
      opts?: { toast?: boolean; saveOpts?: SaveAssignmentPointsNoteOpts }
    ) => {
      setDoc(next);
      docRef.current = next;
      if (!gradeReady) return;
      try {
        if (opts?.toast) showSaving();
        await saveAssignmentPointsNote(next, {
          memberGroupMap,
          // 체크/상태 동기화는 선택 그룹 단위 (클래스 전체 orphan 정리 방지)
          syncRewardGroupId: selectedGroupId ?? undefined,
          ...opts?.saveOpts,
        });
        baselineRef.current = snapshotDoc(next);
        setAssignmentPointsNoteDirty(false);
        if (opts?.toast) showSuccess('저장되었습니다.');
      } catch (e) {
        showError(e instanceof Error ? e.message : '저장에 실패했습니다.');
        throw e;
      }
    },
    [
      gradeReady,
      memberGroupMap,
      selectedGroupId,
      setAssignmentPointsNoteDirty,
      showError,
      showSaving,
      showSuccess,
    ]
  );

  /** TopNav/이탈 저장: payload만. rewards는 체크 시 이미 그룹 단위로 반영됨 */
  const handleSave = useCallback(async () => {
    if (!gradeReady) return;
    await commitDoc(docRef.current, { saveOpts: { syncRewardItemIds: [] } });
  }, [commitDoc, gradeReady]);

  useEffect(() => {
    saveAssignmentPointsNoteRef.current = {
      save: handleSave,
      discard: () => {
        void load();
      },
    };
    return () => {
      saveAssignmentPointsNoteRef.current = null;
    };
  }, [handleSave, load, saveAssignmentPointsNoteRef]);

  const members = useMemo(() => {
    if (!selectedGroupId) return [] as FlatMember[];
    return allMembers.filter((m) => m.groupId === selectedGroupId);
  }, [allMembers, selectedGroupId]);

  const groupMemberIdSet = useMemo(
    () => new Set(members.map((m) => m.id)),
    [members]
  );

  /** 현재 선택 그룹에 배정된 멤버가 있는 과제 (필터 전) */
  const groupScopedItems = useMemo(
    () =>
      doc.items.filter((i) =>
        i.assignedMemberIds.some((id) => groupMemberIdSet.has(id))
      ),
    [doc.items, groupMemberIdSet]
  );

  const generalItems = useMemo(
    () =>
      filterSortKindItems(
        groupScopedItems,
        'general',
        kindListPrefs.general,
        groupMemberIdSet
      ),
    [groupScopedItems, kindListPrefs.general, groupMemberIdSet]
  );
  const projectItems = useMemo(
    () =>
      filterSortKindItems(
        groupScopedItems,
        'project',
        kindListPrefs.project,
        groupMemberIdSet
      ),
    [groupScopedItems, kindListPrefs.project, groupMemberIdSet]
  );
  const routineItems = useMemo(
    () =>
      filterSortKindItems(
        groupScopedItems,
        'routine',
        kindListPrefs.routine,
        groupMemberIdSet
      ),
    [groupScopedItems, kindListPrefs.routine, groupMemberIdSet]
  );
  const challengeItems = useMemo(
    () =>
      filterSortKindItems(
        groupScopedItems,
        'challenge',
        kindListPrefs.challenge,
        groupMemberIdSet
      ),
    [groupScopedItems, kindListPrefs.challenge, groupMemberIdSet]
  );

  const allBoardItems = useMemo(
    () => filterSortKindItems(groupScopedItems, 'all', kindListPrefs.all, groupMemberIdSet),
    [groupScopedItems, kindListPrefs.all, groupMemberIdSet]
  );

  const boardItems =
    boardKind === 'general'
      ? generalItems
      : boardKind === 'project'
        ? projectItems
        : boardKind === 'routine'
          ? routineItems
          : boardKind === 'challenge'
            ? challengeItems
            : allBoardItems;

  const kindBaseCounts = useMemo(() => {
    const counts: Record<AssignmentBoardKind, number> = {
      all: groupScopedItems.length,
      general: 0,
      project: 0,
      routine: 0,
      challenge: 0,
    };
    groupScopedItems.forEach((i) => {
      counts[boardKindOf(i)] += 1;
    });
    return counts;
  }, [groupScopedItems]);

  /** 열: 카드 내용이 다 보이는 최소 폭 기준(좁아지면 열을 줄여 카드를 넓게). 카드는 보드 안에서 세로 스크롤 */
  const boardGridRef = useRef<HTMLDivElement | null>(null);
  const boardHasItems = boardItems.length > 0;
  const [boardCols, setBoardCols] = useState(() => (blockLayoutMode === 'vertical' ? 5 : 3));
  useLayoutEffect(() => {
    const grid = boardGridRef.current;
    if (!grid) return;
    const minW = blockLayoutMode === 'vertical' ? 190 : 360;
    const update = () => {
      const cs = window.getComputedStyle(grid);
      const colGap = parseFloat(cs.columnGap) || 0;
      const availW =
        grid.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
      const cols = Math.max(1, Math.floor((availW + colGap) / (minW + colGap)));
      setBoardCols((prev) => (prev === cols ? prev : cols));
    };
    update();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    ro?.observe(grid);
    return () => ro?.disconnect();
  }, [blockLayoutMode, boardHasItems, loading]);

  /** 무한 스크롤: 보드 끝(센티널)에 닿을 때마다 한 묶음(열 × 4행, 최소 12개)씩 더 그림 */
  const boardChunkSize = Math.max(12, boardCols * 4);
  const [boardVisibleCount, setBoardVisibleCount] = useState(boardChunkSize);
  const boardSentinelRef = useRef<HTMLDivElement | null>(null);
  const boardVisibleItems = boardItems.slice(0, Math.max(boardVisibleCount, boardChunkSize));
  const boardHasMore = boardVisibleItems.length < boardItems.length;

  useEffect(() => {
    boardGridRef.current?.scrollTo({ top: 0 });
    setBoardVisibleCount(0);
  }, [boardKind, selectedGroupId, kindListPrefs]);

  useEffect(() => {
    const root = boardGridRef.current;
    const sentinel = boardSentinelRef.current;
    if (!root || !sentinel || !boardHasMore || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        setBoardVisibleCount((prev) => Math.max(prev, boardChunkSize) + boardChunkSize);
      },
      { root, rootMargin: '0px 0px 160px 0px' }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [boardHasMore, boardChunkSize, boardVisibleItems.length]);

  /** 프로젝트·루틴 상세는 모달로 표시 */
  const drawerItem = useMemo(() => {
    if (!expandedId) return null;
    const found = doc.items.find((i) => i.id === expandedId);
    if (!found || found.kind === 'general' || printSelectionKinds.has(found.kind)) return null;
    return found;
  }, [doc.items, expandedId, printSelectionKinds]);

  const drawerItemId = drawerItem?.id ?? null;
  const closeDetailModal = useCallback(() => {
    setExpandedId(null);
    setAssignmentCheckOpen(false);
    setSelectedId((cur) => (cur === drawerItemId ? null : cur));
  }, [drawerItemId]);
  useEffect(() => {
    if (!drawerItemId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      /** 상세 모달 위에 다른 모달(과제 체크 등)이 떠 있으면 그쪽이 먼저 닫힘 */
      if (document.querySelectorAll('[aria-modal="true"]').length > 1) return;
      closeDetailModal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerItemId, closeDetailModal]);

  /** 프린트 선택용 — 현재 화면에 보이는 전체 과제 */
  const tabItems = useMemo(
    () => [...generalItems, ...projectItems, ...routineItems, ...challengeItems],
    [generalItems, projectItems, routineItems, challengeItems]
  );

  const selectedItem = useMemo(
    () => doc.items.find((i) => i.id === selectedId) ?? null,
    [doc.items, selectedId]
  );

  const categoryById = useMemo(
    () => new Map(doc.settings.categories.map((c) => [c.id, c])),
    [doc.settings.categories]
  );

  const updateKindPrefs = (
    kind: AssignmentBoardKind,
    patch: Partial<KindListPrefs>
  ) => {
    setKindListPrefs((prev) => ({
      ...prev,
      [kind]: { ...prev[kind], ...patch },
    }));
  };

  const renderKindFilter = (kind: AssignmentBoardKind) => {
    const prefs = kindListPrefs[kind];
    const open = filterMenuKind === kind;
    const summary = kindFilterSummary(prefs, kind, categoryById);
    return (
      <div className="cp-apn-list-filter cp-apn-kind-section-filter">
        <button
          type="button"
          className="cp-apn-list-filter-btn"
          aria-expanded={open}
          onClick={() => setFilterMenuKind((cur) => (cur === kind ? null : kind))}
        >
          <span className="cp-apn-list-filter-dot" aria-hidden />
          <span>{summary}</span>
        </button>
        {open ? (
          <>
            <div
              className="cp-apn-list-filter-overlay"
              role="presentation"
              onClick={() => setFilterMenuKind(null)}
            />
            <div className="cp-apn-list-filter-menu" role="listbox">
              <button
                type="button"
                className={`cp-apn-list-filter-option${
                  prefs.filterType === 'all' ? ' is-selected' : ''
                }`}
                onClick={() => {
                  updateKindPrefs(kind, { filterType: 'all' });
                  setFilterMenuKind(null);
                }}
              >
                전체 과제
              </button>
              <button
                type="button"
                className={`cp-apn-list-filter-option${
                  prefs.filterType === 'incomplete' ? ' is-selected' : ''
                }`}
                onClick={() => {
                  updateKindPrefs(kind, { filterType: 'incomplete' });
                  setFilterMenuKind(null);
                }}
              >
                미완료 과제
              </button>
              <div className="cp-apn-list-filter-divider" />
              {(
                [
                  ['createdAt', '생성일'],
                  ['dueDate', '마감일'],
                  ['title', '제목'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`cp-apn-list-filter-option${
                    prefs.sortBy === key ? ' is-selected' : ''
                  }`}
                  onClick={() => {
                    updateKindPrefs(kind, { sortBy: key });
                    setFilterMenuKind(null);
                  }}
                >
                  <span className="cp-apn-list-filter-dot" aria-hidden />
                  {label}
                </button>
              ))}
              {kind === 'general' ? (
                <>
                  <div className="cp-apn-list-filter-divider" />
                  <button
                    type="button"
                    className={`cp-apn-list-filter-option${
                      prefs.categoryId === '' ? ' is-selected' : ''
                    }`}
                    onClick={() => {
                      updateKindPrefs(kind, { categoryId: '' });
                      setFilterMenuKind(null);
                    }}
                  >
                    <span
                      className="cp-apn-list-filter-dot"
                      style={{ background: 'rgba(162, 162, 162, 1)' }}
                      aria-hidden
                    />
                    전체 카테고리
                  </button>
                  {doc.settings.categories.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={`cp-apn-list-filter-option${
                        prefs.categoryId === c.id ? ' is-selected' : ''
                      }`}
                      onClick={() => {
                        updateKindPrefs(kind, { categoryId: c.id });
                        setFilterMenuKind(null);
                      }}
                    >
                      <span
                        className="cp-apn-list-filter-dot"
                        style={{ background: c.color }}
                        aria-hidden
                      />
                      {c.name}
                    </button>
                  ))}
                </>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    );
  };

  const checkSelection: CheckPanelSelection | null = useMemo(() => {
    if (!selectedItem || !assignmentCheckOpen) return null;
    if (selectedItem.kind === 'general') {
      return { kind: 'general', item: selectedItem };
    }
    if (selectedItem.kind === 'project') {
      return {
        kind: 'project',
        item: selectedItem,
        subTaskIndex: Math.min(
          projectSubTaskIndex,
          Math.max(0, selectedItem.subTasks.length - 1)
        ),
        onSubTaskChange: setProjectSubTaskIndex,
      };
    }
    return {
      kind: 'routine',
      item: selectedItem,
      focusDate: routineFocusDate ?? undefined,
    };
  }, [
    selectedItem,
    assignmentCheckOpen,
    projectSubTaskIndex,
    routineFocusDate,
  ]);

  const checkModalOpen = Boolean(
    checkSelection &&
      !(selectedItem && printSelectionKinds.has(selectedItem.kind)) &&
      !reachModalOpen &&
      !dateInputOpen
  );

  const closeCheckModal = useCallback(() => {
    setCheckCloseConfirmOpen(false);
    setCheckClosing(false);
    checkPanelDirtyRef.current = false;
    setCheckPanelDirty(false);
    setAssignmentCheckOpen(false);
    setSelectedId(null);
  }, []);

  const requestCloseCheckModal = useCallback(() => {
    if (checkClosing) return;
    if (checkPanelDirtyRef.current || checkPanelDirty) {
      setCheckCloseConfirmOpen(true);
      return;
    }
    closeCheckModal();
  }, [checkClosing, checkPanelDirty, closeCheckModal]);

  const discardAndCloseCheckModal = useCallback(() => {
    if (checkClosing) return;
    closeCheckModal();
  }, [checkClosing, closeCheckModal]);

  const saveAndCloseCheckModal = useCallback(async () => {
    if (checkClosing) return;
    setCheckClosing(true);
    try {
      await checkSaveActionsRef.current?.save();
      closeCheckModal();
    } catch {
      setCheckClosing(false);
    }
  }, [checkClosing, closeCheckModal]);

  useEffect(() => {
    if (!checkModalOpen) {
      setCheckCloseConfirmOpen(false);
      checkPanelDirtyRef.current = false;
      setCheckPanelDirty(false);
      setCheckClosing(false);
      return undefined;
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (checkCloseConfirmOpen) {
        if (!checkClosing) setCheckCloseConfirmOpen(false);
        return;
      }
      requestCloseCheckModal();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [checkModalOpen, checkCloseConfirmOpen, checkClosing, requestCloseCheckModal]);

  const reconcileGrantedRewardsAfterStructureEdit = useCallback(
    async (
      item: ProjectAssignmentItem | RoutineAssignmentItem
    ): Promise<ProjectAssignmentItem | RoutineAssignmentItem> => {
      const granted = item.rewardGrantedStudentIds ?? [];
      if (granted.length === 0) return item;

      const memberRefs = allMembers.map((m) => ({
        id: m.id,
        name: m.name || '',
        nameChoseongPositions: m.nameChoseongPositions,
        orderIndex: m.orderIndex,
        groupId: m.groupId,
      }));
      const preview = reconcileGrantedAssignmentRewardsInMemory(
        item,
        memberRefs,
        emptyPointItemNoteDoc(workspaceId, currentGradeId)
      );
      const grantedSame =
        preview.nextGrantedStudentIds.length === granted.length &&
        preview.nextGrantedStudentIds.every((id) => granted.includes(id));
      if (grantedSame && preview.revokedCount === 0) return item;

      if (preview.revokedCount > 0) {
        const groupIds = new Set<string>();
        for (const m of preview.revokedMembers) {
          const ref = memberRefs.find((x) => x.id === m.memberId);
          if (ref?.groupId) groupIds.add(ref.groupId);
        }
        const pointDoc = emptyPointItemNoteDoc(workspaceId, currentGradeId);
        const groupIdList = Array.from(groupIds);
        for (let i = 0; i < groupIdList.length; i += 1) {
          const gid = groupIdList[i];
          pointDoc.memberPointsByGroup[gid] = await loadGroupPointsForAssignmentReward(
            workspaceId,
            currentGradeId,
            gid
          );
        }
        const result = reconcileGrantedAssignmentRewardsInMemory(
          item,
          memberRefs,
          pointDoc
        );
        const byGroup = new Map<string, string[]>();
        for (const m of result.revokedMembers) {
          const ref = memberRefs.find((x) => x.id === m.memberId);
          if (!ref?.groupId) continue;
          const list = byGroup.get(ref.groupId) ?? [];
          list.push(m.memberId);
          byGroup.set(ref.groupId, list);
        }
        const groupEntries = Array.from(byGroup.entries());
        for (let i = 0; i < groupEntries.length; i += 1) {
          const [gid, mids] = groupEntries[i];
          const group = result.nextPointDoc.memberPointsByGroup[gid];
          if (!group) continue;
          await saveGroupAssignmentRewardMutations(
            workspaceId,
            currentGradeId,
            gid,
            group,
            Array.from(new Set(mids))
          );
        }
        return {
          ...item,
          rewardGrantedStudentIds: result.nextGrantedStudentIds,
        };
      }

      return {
        ...item,
        rewardGrantedStudentIds: preview.nextGrantedStudentIds,
      };
    },
    [allMembers, currentGradeId, workspaceId]
  );

  const upsertItem = async (item: AssignmentPointsItem) => {
    const now = new Date().toISOString();
    const prev = docRef.current;
    const idx = prev.items.findIndex((x) => x.id === item.id);
    const isStructureEdit =
      idx >= 0 && (item.kind === 'project' || item.kind === 'routine');
    let nextItem = item;
    if (isStructureEdit && (item.kind === 'project' || item.kind === 'routine')) {
      nextItem = await reconcileGrantedRewardsAfterStructureEdit(item);
    }
    const items =
      idx >= 0
        ? prev.items.map((x, i) => (i === idx ? nextItem : x))
        : [...prev.items, nextItem];
    setSelectedId(nextItem.id);
    const hasStatuses =
      (nextItem.kind === 'general' && nextItem.studentStatuses.length > 0) ||
      (nextItem.kind === 'project' &&
        Object.keys(nextItem.studentStatusesBySubTask).length > 0) ||
      (nextItem.kind === 'routine' &&
        nextItem.dateStatuses.some((b) => (b.studentStatuses?.length ?? 0) > 0));
    await commitDoc(
      { ...prev, items, updatedAt: now },
      {
        toast: true,
        saveOpts: isStructureEdit
          ? { replaceRewardItemIds: [nextItem.id] }
          : { syncRewardItemIds: hasStatuses ? [nextItem.id] : [] },
      }
    );
  };

  const handleGrantAssignmentReward = useCallback(
    async (item: ProjectAssignmentItem | RoutineAssignmentItem) => {
      if (!gradeReady || rewardProcessingId || !selectedGroupId) return;
      setRewardProcessingId(item.id);
      try {
        // 현재 선택 그룹에 배정된 멤버만 처리 (다른 그룹 granted 목록은 유지)
        const groupMemberIds = new Set(members.map((m) => m.id));
        const memberRefs = members.map((m) => ({
          id: m.id,
          name: m.name || '',
          nameChoseongPositions: m.nameChoseongPositions,
          orderIndex: m.orderIndex,
          groupId: m.groupId,
        }));
        const otherGroupGranted = (item.rewardGrantedStudentIds ?? []).filter(
          (id) => !groupMemberIds.has(id)
        );

        // 1차: 메모리만으로 지급/회수 대상 판별 (DB 미접근)
        const previewPointDoc = emptyPointItemNoteDoc(workspaceId, currentGradeId);
        const preview =
          item.kind === 'project'
            ? processProjectAssignmentRewardsInMemory(item, memberRefs, previewPointDoc)
            : processRoutineAssignmentRewardsInMemory(item, memberRefs, previewPointDoc);

        if (!preview.success) {
          showError(preview.message);
          return;
        }

        // 빈 문서 미리보기는 잠긴 아이템/회수된 코인을 모름 → 기지급+자격 유지 시 실데이터 확인
        const needsPointDocCheck = assignmentRewardNeedsPointDocCheck(item, memberRefs);
        const hasRewardMutation = preview.grantedCount > 0 || preview.revokedCount > 0;
        setRewardResultTitle(
          item.kind === 'project' ? '프로젝트 과제 보상 처리 결과' : '루틴 과제 보상 처리 결과'
        );

        if (!hasRewardMutation && !needsPointDocCheck) {
          setRewardResult({
            ...preview,
            nextGrantedStudentIds: [
              ...otherGroupGranted,
              ...preview.nextGrantedStudentIds,
            ],
          });
          return;
        }

        // 2차: 그룹 단위 코인/아이템만 로드·반영 (학년 전체 DELETE+INSERT 없음)
        const groupPoints = await loadGroupPointsForAssignmentReward(
          workspaceId,
          currentGradeId,
          selectedGroupId
        );
        const pointDoc = {
          ...emptyPointItemNoteDoc(workspaceId, currentGradeId),
          memberPointsByGroup: { [selectedGroupId]: groupPoints },
        };
        const result =
          item.kind === 'project'
            ? processProjectAssignmentRewardsInMemory(item, memberRefs, pointDoc)
            : processRoutineAssignmentRewardsInMemory(item, memberRefs, pointDoc);

        if (!result.success) {
          showError(result.message);
          return;
        }

        const resultMutated = result.grantedCount > 0 || result.revokedCount > 0;
        if (!resultMutated) {
          setRewardResult({
            ...result,
            nextGrantedStudentIds: [
              ...otherGroupGranted,
              ...result.nextGrantedStudentIds,
            ],
          });
          return;
        }

        const mutatedMemberIds = [
          ...result.newlyGrantedMembers.map((m) => m.memberId),
          ...result.revokedMembers.map((m) => m.memberId),
          ...result.holderMembers.map((m) => m.memberId),
        ];
        const uniqueMutated = Array.from(new Set(mutatedMemberIds));
        const nextGroup =
          result.nextPointDoc.memberPointsByGroup[selectedGroupId] ?? groupPoints;
        await saveGroupAssignmentRewardMutations(
          workspaceId,
          currentGradeId,
          selectedGroupId,
          nextGroup,
          uniqueMutated.length > 0 ? uniqueMutated : members.map((m) => m.id)
        );

        const mergedGranted = [
          ...otherGroupGranted,
          ...result.nextGrantedStudentIds,
        ];
        const now = new Date().toISOString();
        const prev = docRef.current;
        const nextItem =
          item.kind === 'project'
            ? ({
                ...item,
                rewardGrantedStudentIds: mergedGranted,
                updatedAt: now,
              } satisfies ProjectAssignmentItem)
            : ({
                ...item,
                rewardGrantedStudentIds: mergedGranted,
                updatedAt: now,
              } satisfies RoutineAssignmentItem);
        const items = prev.items.map((x) => (x.id === item.id ? nextItem : x));
        await commitDoc(
          { ...prev, items, updatedAt: now },
          { saveOpts: { syncRewardItemIds: [], skipNotify: true } }
        );
        setRewardResult({
          ...result,
          nextGrantedStudentIds: mergedGranted,
        });
      } catch (e) {
        showError(e instanceof Error ? e.message : '보상 처리 중 오류가 발생했습니다.');
      } finally {
        setRewardProcessingId(null);
      }
    },
    [
      commitDoc,
      currentGradeId,
      gradeReady,
      members,
      rewardProcessingId,
      selectedGroupId,
      showError,
      workspaceId,
    ]
  );

  const openEditItem = (item: AssignmentPointsItem) => {
    setSelectedId(item.id);
    if (item.kind === 'general') {
      setGeneralModal({ open: true, mode: 'edit', item });
    } else if (item.kind === 'project') {
      setProjectModal({ open: true, mode: 'edit', item, challenge: isChallengeAssignment(item) });
    } else {
      setRoutineModal({ open: true, mode: 'edit', item });
    }
  };

  const deleteItem = async (item: AssignmentPointsItem) => {
    const challenge = isChallengeAssignment(item);
    const message = challenge
      ? `「${item.title || '제목 없음'}」 챌린지 과제를 삭제할까요?\n학생 제출 기록과 인증 사진도 모두 삭제되며 되돌릴 수 없어요.`
      : `「${item.title || '제목 없음'}」 과제를 삭제할까요?`;
    if (!window.confirm(message)) return;
    if (challenge) {
      try {
        await deleteChallengeProjects([item.challenge.projectId]);
      } catch (e) {
        showError(formatChallengeError(e));
        return;
      }
    }
    const now = new Date().toISOString();
    const prev = docRef.current;
    setSelectedId((cur) => (cur === item.id ? null : cur));
    setExpandedId((cur) => (cur === item.id ? null : cur));
    setAssignmentCheckOpen(false);
    await commitDoc(
      {
        ...prev,
        items: prev.items.filter((x) => x.id !== item.id),
        updatedAt: now,
      },
      {
        toast: true,
        saveOpts: {
          syncRewardItemIds: [],
          deletedRewardItemIds: [item.id],
        },
      }
    );
  };

  const openDuplicateItem = (item: AssignmentPointsItem) => {
    // 복제 = 새로 만들기 → 프로젝트·루틴·챌린지는 이용권별 권한 확인
    if (item.kind !== 'general' && requireFeature('assignment_advanced')) return;
    setSelectedId(item.id);
    if (item.kind === 'general') {
      setGeneralModal({ open: true, mode: 'copy', item });
    } else if (item.kind === 'project') {
      setProjectModal({ open: true, mode: 'copy', item, challenge: isChallengeAssignment(item) });
    } else {
      setRoutineModal({ open: true, mode: 'copy', item });
    }
  };

  const applyCheckStatuses = async (payload: {
    kind: 'general' | 'project' | 'routine';
    itemId: string;
    statuses: AssignmentStudentStatus[];
    subTaskId?: string;
    date?: string;
  }) => {
    const now = new Date().toISOString();
    const scopeMemberIds = groupMemberIdSet;
    const prev = docRef.current;
    const next: AssignmentPointsNoteDoc = {
      ...prev,
      updatedAt: now,
      items: prev.items.map((item) => {
        if (item.id !== payload.itemId) return item;
        if (item.kind === 'general' && payload.kind === 'general') {
          return {
            ...item,
            studentStatuses: mergeScopedStudentStatuses(
              item.studentStatuses,
              payload.statuses,
              scopeMemberIds
            ),
            updatedAt: now,
          };
        }
        if (item.kind === 'project' && payload.kind === 'project' && payload.subTaskId) {
          const nextMap = { ...item.studentStatusesBySubTask };
          for (const st of payload.statuses) {
            nextMap[`${payload.subTaskId}:${st.studentId}`] = st;
          }
          return { ...item, studentStatusesBySubTask: nextMap, updatedAt: now };
        }
        if (item.kind === 'routine' && payload.kind === 'routine' && payload.date) {
          const dateStatuses = [...item.dateStatuses];
          const idx = dateStatuses.findIndex((b) => b.date === payload.date);
          const prevBucket = idx >= 0 ? dateStatuses[idx] : undefined;
          const bucket = {
            date: payload.date,
            studentStatuses: mergeScopedStudentStatuses(
              prevBucket?.studentStatuses ?? [],
              payload.statuses,
              scopeMemberIds
            ),
            isCustomDate: prevBucket?.isCustomDate,
          };
          if (idx >= 0) dateStatuses[idx] = bucket;
          else dateStatuses.push(bucket);
          return { ...item, dateStatuses, updatedAt: now };
        }
        return item;
      }),
    };
    await commitDoc(next, {
      saveOpts: { syncRewardItemIds: [payload.itemId] },
    });
  };

  const challengeProjectKey = useMemo(
    () =>
      doc.items
        .filter(isChallengeAssignment)
        .map((i) => i.challenge.projectId)
        .sort()
        .join(','),
    [doc.items]
  );

  /** 학생 제출 → 미확인 칸을 '제출'로 표시(확인은 교사가 직접). head 비교로 변경 시에만 전체 조회 */
  const syncChallengeSubmissions = useCallback(
    async (force = false) => {
      if (!gradeReady) return;
      const prev = docRef.current;
      const linked = prev.items.filter(isChallengeAssignment);
      if (linked.length === 0) {
        setChallengeSummaries([]);
        return;
      }
      let rows: ChallengeSubmissionSummary[];
      try {
        rows = await fetchChallengeSubmissionSummaries({
          workspaceId,
          gradeId: currentGradeId,
          projectIds: linked.map((i) => i.challenge.projectId),
          force,
        });
      } catch {
        return;
      }
      setChallengeSummaries(rows);
      if (isDirtyRef.current) return;
      const latest = docRef.current;
      const now = new Date().toISOString();
      const touched: string[] = [];
      const items = latest.items.map((item) => {
        if (!isChallengeAssignment(item)) return item;
        const assigned = new Set(item.assignedMemberIds);
        let nextMap: Record<string, AssignmentStudentStatus> | null = null;
        for (const r of rows) {
          if (r.projectId !== item.challenge.projectId || !assigned.has(r.memberId)) continue;
          const sub = item.subTasks.find((s) => s.id === r.itemId);
          if (!sub) continue;
          const key = `${sub.id}:${r.memberId}`;
          const cur = item.studentStatusesBySubTask[key];
          if (cur && cur.status !== 'pending') continue;
          nextMap = nextMap ?? { ...item.studentStatusesBySubTask };
          nextMap[key] = {
            ...cur,
            studentId: r.memberId,
            status: 'submitted',
            earnedPoints: sub.points,
            updatedAt: now,
          };
        }
        if (!nextMap) return item;
        touched.push(item.id);
        return { ...item, studentStatusesBySubTask: nextMap, updatedAt: now };
      });
      if (touched.length === 0) return;
      try {
        await commitDoc(
          { ...latest, items, updatedAt: now },
          { saveOpts: { syncRewardItemIds: touched } }
        );
      } catch {
        /* commitDoc 에서 오류 표시 */
      }
    },
    [commitDoc, currentGradeId, gradeReady, workspaceId]
  );

  useEffect(() => {
    if (loading || !challengeProjectKey) return;
    void syncChallengeSubmissions();
  }, [loading, challengeProjectKey, syncChallengeSubmissions]);

  useEffect(() => {
    if (!challengeProjectKey) return undefined;
    let last = 0;
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      const t = Date.now();
      if (t - last < 20000) return;
      last = t;
      void syncChallengeSubmissions();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [challengeProjectKey, syncChallengeSubmissions]);

  /** itemId → subTaskId → 선택 그룹 제출 수 */
  const challengeGroupCounts = useMemo(() => {
    const byProject = new Map<string, string>();
    doc.items.forEach((i) => {
      if (isChallengeAssignment(i)) byProject.set(i.challenge.projectId, i.id);
    });
    const out: Record<string, Record<string, number>> = {};
    challengeSummaries.forEach((r) => {
      const itemId = byProject.get(r.projectId);
      if (!itemId || !groupMemberIdSet.has(r.memberId)) return;
      const m = (out[itemId] = out[itemId] ?? {});
      m[r.itemId] = (m[r.itemId] ?? 0) + 1;
    });
    return out;
  }, [challengeSummaries, doc.items, groupMemberIdSet]);

  const challengeEditorItem = useMemo(() => {
    const found = challengeEditorItemId
      ? doc.items.find((i) => i.id === challengeEditorItemId)
      : null;
    return isChallengeAssignment(found) ? found : null;
  }, [challengeEditorItemId, doc.items]);

  const challengeEditorSubmissionCounts = useMemo(() => {
    const out: Record<string, number> = {};
    const pid = challengeEditorItem?.challenge.projectId;
    if (!pid) return out;
    challengeSummaries.forEach((r) => {
      if (r.projectId === pid) out[r.itemId] = (out[r.itemId] ?? 0) + 1;
    });
    return out;
  }, [challengeEditorItem, challengeSummaries]);

  const drawerChallengeProjectId = isChallengeAssignment(drawerItem) ? drawerItem.challenge.projectId : '';
  const drawerChallengeColor = isChallengeAssignment(drawerItem) ? drawerItem.color : '';
  const drawerChallengeCoverUrl =
    isChallengeAssignment(drawerItem) && drawerItem.coverImageId
      ? ((doc.coverImages ?? []).find((c) => c.id === drawerItem.coverImageId)?.url ?? '')
      : '';
  useEffect(() => {
    if (!drawerChallengeProjectId || challengeEditorItemId) return undefined;
    let cancelled = false;
    const apply = (
      items: Array<{ id: string; thumbnailUrl: string | null; isVisible: boolean; content: { kicker: string; missions: unknown[] } }>
    ) => {
      if (cancelled) return;
      const map: Record<string, { thumbnailUrl: string | null; kicker: string; isVisible: boolean; missionCount: number }> = {};
      items.forEach((i) => {
        map[i.id] = {
          thumbnailUrl: i.thumbnailUrl,
          kicker: i.content.kicker,
          isVisible: i.isVisible,
          missionCount: i.content.missions.length,
        };
      });
      setChallengeCardInfo((prev) => ({ ...prev, [drawerChallengeProjectId]: map }));
    };
    void fetchChallengeBundle(drawerChallengeProjectId, { onCached: (b) => apply(b.items) })
      .then((b) => {
        if (!b) return;
        apply(b.items);
        const theme = normalizeChallengeThemeColor(drawerChallengeColor);
        const themeStale = !!theme && b.project.themeColor !== theme;
        const coverStale = (normalizeChallengeIntro(b.project.intro).thumbnailUrl ?? '') !== drawerChallengeCoverUrl;
        if (!cancelled && (themeStale || coverStale)) {
          void updateChallengeProjectMeta(drawerChallengeProjectId, {
            ...(themeStale ? { themeColor: theme } : {}),
            ...(coverStale ? { coverUrl: drawerChallengeCoverUrl } : {}),
          }).catch(() => undefined);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [drawerChallengeProjectId, drawerChallengeColor, drawerChallengeCoverUrl, challengeEditorItemId]);

  const challengeShareItem = useMemo(() => {
    const found = challengeShareItemId ? doc.items.find((i) => i.id === challengeShareItemId) : null;
    return isChallengeAssignment(found) ? found : null;
  }, [challengeShareItemId, doc.items]);


  const challengeCoverUrlOf = (it: ProjectAssignmentItem): string =>
    it.coverImageId ? ((doc.coverImages ?? []).find((c) => c.id === it.coverImageId)?.url ?? '') : '';

  const handleProjectModalSave = async (item: ProjectAssignmentItem) => {
    const closeModal = () => setProjectModal((s) => ({ ...s, open: false }));
    if (!projectModal.challenge) {
      await upsertItem(item);
      closeModal();
      return;
    }
    try {
      if (projectModal.mode === 'edit' && isChallengeAssignment(item)) {
        const before = projectModal.item;
        await upsertItem(item);
        const titleChanged = item.title !== (before?.title ?? '');
        const colorChanged = item.color !== (before?.color ?? '');
        const coverChanged = (item.coverImageId ?? '') !== (before?.coverImageId ?? '');
        if (titleChanged || colorChanged || coverChanged) {
          await updateChallengeProjectMeta(item.challenge.projectId, {
            ...(titleChanged ? { title: item.title } : {}),
            ...(colorChanged ? { themeColor: item.color } : {}),
            ...(coverChanged ? { coverUrl: challengeCoverUrlOf(item) } : {}),
          });
        }
        closeModal();
        return;
      }
      const project = await createChallengeProject({
        workspaceId,
        gradeId: currentGradeId,
        assignmentItemId: item.id,
        title: item.title,
        themeColor: item.color,
      });
      const nextItem: ProjectAssignmentItem = { ...item, challenge: { projectId: project.id } };
      const createdCoverUrl = challengeCoverUrlOf(item);
      if (createdCoverUrl) {
        void updateChallengeProjectMeta(project.id, { coverUrl: createdCoverUrl }).catch(() => undefined);
      }
      const source = projectModal.mode === 'copy' ? projectModal.item : null;
      if (isChallengeAssignment(source)) {
        await duplicateChallengeItems({
          sourceProjectId: source.challenge.projectId,
          target: project,
          targetItemIds: nextItem.subTasks.map((s) => s.id),
          titles: nextItem.subTasks.map((s) => s.title),
        });
      } else {
        await syncChallengeItemsFromSubTasks({
          projectId: project.id,
          subTasks: nextItem.subTasks,
          defaultContent: defaultChallengeContent,
        });
      }
      await upsertItem(nextItem);
      closeModal();
      setChallengeEditorItemId(nextItem.id);
    } catch (e) {
      showError(formatChallengeError(e));
    }
  };

  const handleChallengeEditorSaved = async (
    subTasks: ProjectAssignmentItem['subTasks']
  ) => {
    if (!challengeEditorItemId) return;
    const cur = docRef.current.items.find((i) => i.id === challengeEditorItemId);
    if (!cur || cur.kind !== 'project') return;
    await upsertItem({
      ...cur,
      subTasks,
      studentStatusesBySubTask: pruneProjectStudentStatuses(
        cur.studentStatusesBySubTask,
        subTasks,
        cur.assignedMemberIds
      ),
      updatedAt: new Date().toISOString(),
    });
  };

  const resetChallengeSubmittedMarks = async (itemId: string, subTaskId: string) => {
    const found = docRef.current.items.find((x) => x.id === itemId);
    if (!isChallengeAssignment(found)) return;
    const item = found;
    const prefix = `${subTaskId}:`;
    const now = new Date().toISOString();
    const nextMap = { ...item.studentStatusesBySubTask };
    let changed = false;
    Object.entries(nextMap).forEach(([key, st]) => {
      if (!key.startsWith(prefix) || !st) return;
      if (st.status === 'pending' && !st.earnedPoints) return;
      changed = true;
      if (st.memo) {
        nextMap[key] = { studentId: st.studentId, status: 'pending', earnedPoints: 0, memo: st.memo, updatedAt: now };
      } else {
        delete nextMap[key];
      }
    });
    if (changed) {
      const prev = docRef.current;
      try {
        await commitDoc(
          {
            ...prev,
            updatedAt: now,
            items: prev.items.map((x) =>
              x.id === item.id ? { ...item, studentStatusesBySubTask: nextMap, updatedAt: now } : x
            ),
          },
          { saveOpts: { syncRewardItemIds: [item.id] } }
        );
      } catch {
        /* commitDoc 에서 오류 표시 */
      }
    }
    await syncChallengeSubmissions(true);
  };

  const openCreateKind = (kind: CreateKind) => {
    // 일반 과제 외(프로젝트·루틴·챌린지)는 이용권별 권한으로 막는다(잠금이면 이용권 안내 모달)
    if (kind !== 'general' && requireFeature('assignment_advanced')) return;
    if (kind === 'general') setGeneralModal({ open: true, mode: 'new', item: null });
    else if (kind === 'project') setProjectModal({ open: true, mode: 'new', item: null });
    else if (kind === 'challenge') {
      setProjectModal({ open: true, mode: 'new', item: null, challenge: true });
    }
    else setRoutineModal({ open: true, mode: 'new', item: null });
  };

  const boardSelectionMode = printSelectionKinds.size > 0;

  const toggleBoardPrintSelectionMode = () => {
    if (boardSelectionMode) {
      setPrintSelectionKinds(new Set());
      setPrintSelectedIds(new Set());
      return;
    }
    setPrintSelectionKinds(new Set<AssignmentPointsKind>(['general', 'project', 'routine']));
  };

  const togglePrintSelectId = (id: string) => {
    setPrintSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exportSelectedAssignments = (kind?: AssignmentPointsKind) => {
    if (
      (kind ? !printSelectionKinds.has(kind) : printSelectionKinds.size === 0) ||
      printSelectedIds.size === 0 ||
      isExportingPrint
    ) {
      return;
    }
    setIsExportingPrint(true);
    try {
      const selected = tabItems.filter(
        (a) => printSelectedIds.has(a.id) && (!kind || a.kind === kind)
      );
      if (selected.length === 0) return;
      // 공유과제: 현재 선택 그룹 멤버만 집계·인쇄
      const groupIdSet = new Set(members.map((m) => m.id));
      const groupPrintIds = (assignedIds: string[]) =>
        assignedIds.filter((id) => groupIdSet.has(id));

      const projectSelected = selected.filter(
        (a): a is ProjectAssignmentItem => a.kind === 'project'
      );
      const otherSelected = selected.filter(
        (ass): ass is GeneralAssignmentItem | RoutineAssignmentItem =>
          ass.kind === 'general' || ass.kind === 'routine'
      );

      if (projectSelected.length > 0) {
        const exportData = projectSelected.map((project) => {
          const printIds = groupPrintIds(project.assignedMemberIds);
          const subTasks = project.subTasks.map((sub) => {
            const statuses = printIds.map((sid) => {
              const key = `${sub.id}:${sid}`;
              return (
                project.studentStatusesBySubTask[key] ?? {
                  studentId: sid,
                  status: 'pending' as const,
                  earnedPoints: 0,
                }
              );
            });
            return {
              subTaskTitle: sub.title || '하위과제',
              subTaskPoints: sub.points || 0,
              students: buildPrintStudents(printIds, statuses, members),
            };
          });
          const totalPoints = subTasks.reduce((sum, st) => sum + st.subTaskPoints, 0);
          return {
            project,
            dueDateText: formatAssignmentDueDateFromIso(project.dueDate) || '마감일 없음',
            basePoints: totalPoints,
            rewardCoins: project.rewardEnabled ? project.rewardCoins || 0 : 0,
            rewardItemCardCount: project.rewardEnabled
              ? project.rewardItemIds?.length || 0
              : 0,
            subTasks,
          };
        });
        openAssignmentPrintWindow(buildProjectAssignmentsExportHtml(exportData));
      }

      if (otherSelected.length > 0) {
        const exportData = otherSelected.map((ass) => {
          const printIds = groupPrintIds(ass.assignedMemberIds);
          if (ass.kind === 'routine') {
            const dates = listAllRoutineDates(ass);
            const today = new Date();
            const t = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            const dateIso =
              dates.find((d) => d === t) ||
              dates.find((d) => d >= t) ||
              dates[dates.length - 1] ||
              '';
            const bucket = ass.dateStatuses.find((b) => b.date === dateIso);
            const statuses = (bucket?.studentStatuses || []).filter((s) =>
              groupIdSet.has(s.studentId)
            );
            const hydrated =
              statuses.length > 0
                ? statuses
                : printIds.map((sid) => ({
                    studentId: sid,
                    status: 'pending' as const,
                    earnedPoints: 0,
                  }));
            const categoryName =
              doc.settings.categories.find((c) => c.id === ass.categoryId)?.name ||
              '루틴 과제';
            return {
              assignment: ass,
              categoryName,
              dueDateText: dateIso
                ? `과제일 ${formatAssignmentDueDateFromIso(dateIso)}`
                : '마감일 없음',
              basePoints: ass.basePoints || 0,
              rewardCoins: ass.rewardEnabled ? ass.rewardCoins || 0 : 0,
              rewardItemCardCount: ass.rewardEnabled ? ass.rewardItemIds?.length || 0 : 0,
              students: buildPrintStudents(printIds, hydrated, members),
            };
          }
          const categoryName =
            doc.settings.categories.find((c) => c.id === ass.categoryId)?.name ||
            '전체 카테고리';
          return {
            assignment: ass,
            categoryName,
            dueDateText: formatAssignmentDueDateFromIso(ass.dueDate) || '마감일 없음',
            basePoints: ass.basePoints || 0,
            rewardCoins: ass.rewardEnabled ? ass.rewardCoins || 0 : 0,
            rewardItemCardCount: ass.rewardEnabled ? ass.rewardItemIds?.length || 0 : 0,
            students: buildPrintStudents(
              printIds,
              ass.studentStatuses.filter((s) => groupIdSet.has(s.studentId)),
              members
            ),
          };
        });
        openAssignmentPrintWindow(buildGeneralAssignmentsExportHtml(exportData));
      }
    } catch (error) {
      console.error('선택 과제 프린트 실패:', error);
      alert('선택 과제 프린트에 실패했습니다.');
    } finally {
      setIsExportingPrint(false);
    }
  };

  const applySettings = async (settings: AssignmentPointsSettings) => {
    const now = new Date().toISOString();
    setSettingsOpen(false);
    await commitDoc(
      { ...docRef.current, settings, updatedAt: now },
      { toast: true, saveOpts: { syncRewardItemIds: [] } }
    );
  };

  const coverImages = doc.coverImages ?? [];
  const coverImageById = useMemo(() => {
    const map = new Map<string, AssignmentCoverImage>();
    for (const img of coverImages) {
      map.set(img.id, img);
    }
    return map;
  }, [coverImages]);

  useEffect(() => {
    preloadAssignmentCoverImages(coverImages);
  }, [coverImages]);

  const handleCoverImagesChange = useCallback(
    (nextImages: AssignmentCoverImage[]) => {
      const limited = nextImages.slice(0, ASSIGNMENT_COVER_IMAGE_MAX);
      const ids = new Set(limited.map((c) => c.id));
      const prev = docRef.current;
      const items = prev.items.map((item) =>
        item.coverImageId && !ids.has(item.coverImageId)
          ? { ...item, coverImageId: undefined }
          : item
      );
      void commitDoc(
        {
          ...prev,
          coverImages: limited,
          items,
          updatedAt: new Date().toISOString(),
        },
        { saveOpts: { syncRewardItemIds: [] } }
      );
    },
    [commitDoc]
  );

  const renderAssignmentCard = (item: AssignmentPointsItem, inDrawer = false) => (
    <AssignmentBlockCard
      key={item.id}
      item={item}
      coverImage={
        item.coverImageId ? coverImageById.get(item.coverImageId) : undefined
      }
      category={
        item.kind !== 'project' && item.categoryId
          ? categoryById.get(item.categoryId)
          : undefined
      }
      selected={selectedId === item.id || expandedId === item.id}
      isShared={sharedAssignmentIds.has(item.id)}
      expanded={inDrawer && expandedId === item.id}
      selectionMode={!inDrawer && printSelectionKinds.has(item.kind)}
      printSelected={printSelectedIds.has(item.id)}
      onTogglePrintSelect={() => togglePrintSelectId(item.id)}
      activeSubTaskId={
        item.kind === 'project' &&
        expandedId === item.id &&
        selectedId === item.id
          ? item.subTasks[
              Math.min(
                projectSubTaskIndex,
                Math.max(0, item.subTasks.length - 1)
              )
            ]?.id ?? null
          : null
      }
      activeDate={
        item.kind === 'routine' &&
        expandedId === item.id &&
        selectedId === item.id
          ? routineFocusDate
          : null
      }
      onSelect={() => {
        if (inDrawer) return;
        if (item.kind === 'general') {
          setSelectedId(item.id);
          setExpandedId(null);
          setAssignmentCheckOpen(true);
          return;
        }
        // 프로젝트·루틴: 블럭 클릭 시 하위 섹션만 토글 (체크 모달은 하위항목 선택 시)
        if (expandedId === item.id) {
          setExpandedId(null);
          setAssignmentCheckOpen(false);
          setSelectedId((cur) => (cur === item.id ? null : cur));
          return;
        }
        setExpandedId(item.id);
        setAssignmentCheckOpen(false);
        setSelectedId((cur) => (cur === item.id ? cur : null));
        setProjectSubTaskIndex(0);
        setRoutineFocusDate(null);
      }}
      onCollapse={() => {
        setExpandedId(null);
        setAssignmentCheckOpen(false);
        setSelectedId((cur) => (cur === item.id ? null : cur));
      }}
      onOpenMonitoring={() => {
        setSelectedId(item.id);
        setExpandedId(item.id);
        setAssignmentCheckOpen(false);
        setReachModalOpen(true);
      }}
      rewardProcessing={rewardProcessingId === item.id}
      onGrantReward={
        item.kind === 'project' || item.kind === 'routine'
          ? () => {
              void handleGrantAssignmentReward(item);
            }
          : undefined
      }
      onSelectSubTask={(_subTaskId, index) => {
        setSelectedId(item.id);
        setExpandedId(item.id);
        setProjectSubTaskIndex(index);
        setAssignmentCheckOpen(true);
      }}
      onSelectDate={(date) => {
        setSelectedId(item.id);
        setExpandedId(item.id);
        setRoutineFocusDate(date);
        setAssignmentCheckOpen(true);
      }}
      onAddCustomDate={() => {
        setSelectedId(item.id);
        setExpandedId(item.id);
        setAssignmentCheckOpen(false);
        setDateInputOpen(true);
      }}
      onEdit={() => openEditItem(item)}
      onDuplicate={() => openDuplicateItem(item)}
      onDelete={() => deleteItem(item)}
      memberIdSet={groupMemberIdSet}
      challengeTools={
        isChallengeAssignment(item)
          ? {
              submittedCountBySubTask: challengeGroupCounts[item.id] ?? {},
              cardInfoBySubTask: challengeCardInfo[item.challenge.projectId],
              onEdit: () => setChallengeEditorItemId(item.id),
              onShare: () => setChallengeShareItemId(item.id),
              onOpenSubmissions: (subTaskId: string) => {
                const index = item.subTasks.findIndex((s) => s.id === subTaskId);
                setSelectedId(item.id);
                setExpandedId(item.id);
                setProjectSubTaskIndex(Math.max(0, index));
                setAssignmentCheckOpen(true);
                void syncChallengeSubmissions();
              },
            }
          : undefined
      }
    />
  );

  if (!gradeReady) {
    return (
      <div className="cp-apn-page">
        <header className="cp-apn-page-header">
          <h1>과제 관리</h1>
        </header>
        <div className="cp-apn-integrated-hint">
          <p>과제 관리는 클래스별로 관리됩니다.</p>
          <p>상단에서 클래스를 선택해 주세요. (통합 보기에서는 사용할 수 없습니다.)</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`cp-apn-page${
        blockLayoutMode === 'vertical' ? ' is-layout-vertical' : ' is-layout-horizontal'
      }`}
    >
      {saveToastState ? <AutoStatusToast state={saveToastState} /> : null}
      <header className="cp-apn-page-header">
        <div className="cp-apn-page-title-row">
          <div className="cp-apn-page-title-heading">
            <h1>과제 관리</h1>
            <div className="cp-todo-note-view-toggle" role="tablist" aria-label="과제 블럭 표시 모드">
              <button
                type="button"
                role="tab"
                aria-selected={blockLayoutMode === 'horizontal'}
                className={`cp-todo-note-view-btn${
                  blockLayoutMode === 'horizontal' ? ' is-active' : ''
                }`}
                onClick={() => {
                  setBlockLayoutMode('horizontal');
                  saveAssignmentBlockLayoutMode('horizontal');
                }}
              >
                가로모드
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={blockLayoutMode === 'vertical'}
                className={`cp-todo-note-view-btn${
                  blockLayoutMode === 'vertical' ? ' is-active' : ''
                }`}
                onClick={() => {
                  setBlockLayoutMode('vertical');
                  saveAssignmentBlockLayoutMode('vertical');
                }}
              >
                세로모드
              </button>
            </div>
          </div>
          <NoteGroupSelector
            groups={groups}
            selectedGroupId={selectedGroupId}
            onChange={(id) => {
              setSelectedGroupId(id);
              saveNoteSelectedGroupId('assignmentPoints', workspaceId, currentGradeId, id);
              setSelectedId(null);
              setExpandedId(null);
              setAssignmentCheckOpen(false);
              setPrintSelectionKinds(new Set());
              setPrintSelectedIds(new Set());
              setProjectSubTaskIndex(0);
              setRoutineFocusDate(null);
            }}
          />
        </div>
        <div className="cp-apn-page-header-actions">
          <div className="cp-apn-header-icon-btn-wrap">
            <button
              type="button"
              className="cp-apn-header-icon-btn"
              onClick={() => setTableModalOpen(true)}
              aria-label="과제 관리 테이블"
            >
              <img src={`${process.env.PUBLIC_URL || ''}/table.svg`} alt="" width={28} height={28} decoding="async" />
            </button>
            <span className="cp-apn-header-icon-tooltip" role="tooltip">
              과제 관리 테이블
            </span>
          </div>
          <div className="cp-apn-header-icon-btn-wrap">
            <button
              type="button"
              className="cp-apn-header-icon-btn"
              onClick={() => setIncompleteReportMode('byMember')}
              aria-label="멤버별 미완료 과제"
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/membersprint.svg`}
                alt=""
                width={28}
                height={28}
                decoding="async"
              />
            </button>
            <span className="cp-apn-header-icon-tooltip" role="tooltip">
              멤버별 미완료 과제
            </span>
          </div>
          <div className="cp-apn-header-icon-btn-wrap">
            <button
              type="button"
              className="cp-apn-header-icon-btn"
              onClick={() => setIncompleteReportMode('byAssignment')}
              aria-label="과제별 미완료 멤버"
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/tasksprint.svg`}
                alt=""
                width={28}
                height={28}
                decoding="async"
              />
            </button>
            <span className="cp-apn-header-icon-tooltip" role="tooltip">
              과제별 미완료 멤버
            </span>
          </div>
          <div className="cp-apn-header-icon-btn-wrap">
            <button
              type="button"
              className="cp-apn-header-icon-btn"
              onClick={() => setSettingsOpen(true)}
              aria-label="설정"
            >
              <BasicSettingIcon />
            </button>
            <span className="cp-apn-header-icon-tooltip" role="tooltip">
              설정
            </span>
          </div>
        </div>
      </header>

      {loadError ? <p className="cp-apn-error">{loadError}</p> : null}

      <div className="cp-apn-split">
        <div className="cp-apn-kind-sections is-board">
          <section className="cp-apn-kind-section cp-apn-board" aria-label="과제 보드">
            <div className="cp-apn-section-head cp-apn-kind-bar cp-apn-board-bar">
              <div className="cp-apn-kind-bar-title">
                <span className="cp-apn-section-head-title">과제 보드</span>
                <span className="cp-apn-kind-section-count">{boardItems.length}</span>
              </div>
              <div className="cp-apn-board-kind-tabs" role="tablist" aria-label="과제 유형">
                {ASSIGNMENT_BOARD_KINDS.map(({ kind, label }) => (
                  <button
                    key={kind}
                    type="button"
                    role="tab"
                    aria-selected={boardKind === kind}
                    className={`cp-apn-board-kind-tab${boardKind === kind ? ' is-active' : ''}`}
                    onClick={() => {
                      setBoardKind(kind);
                      saveAssignmentBoardKind(kind);
                      setFilterMenuKind(null);
                    }}
                  >
                    {label}
                    <span className="cp-apn-board-kind-tab-count">{kindBaseCounts[kind]}</span>
                  </button>
                ))}
              </div>
              <div className="cp-apn-toolbar-actions">
                <button
                  type="button"
                  className={`cp-apn-circle-toggle-btn${boardSelectionMode ? ' is-active' : ''}`}
                  onClick={toggleBoardPrintSelectionMode}
                  title={boardSelectionMode ? '선택모드 취소' : '선택모드'}
                  aria-pressed={boardSelectionMode}
                  aria-label={boardSelectionMode ? '선택모드 취소' : '선택모드'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M20 6L9 17l-5-5"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  className="cp-apn-icon-action-btn"
                  onClick={() => exportSelectedAssignments()}
                  disabled={!boardSelectionMode || printSelectedIds.size === 0 || isExportingPrint}
                  title="프린트"
                  aria-label="프린트"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z"
                    />
                  </svg>
                </button>
                {ASSIGNMENT_CREATE_KINDS.map(({ kind, label, title }) => (
                  <span key={kind} role="group" aria-label={title} className="cp-apn-board-add-wrap">
                    <button
                      type="button"
                      className={`cp-apn-add-btn cp-apn-board-add-btn cp-apn-board-add-btn--${kind}${
                        kind !== 'general' && advancedAssignmentLocked ? ' is-plan-locked' : ''
                      }`}
                      onClick={() => openCreateKind(kind)}
                      disabled={loading}
                      title={
                        kind !== 'general' && advancedAssignmentLocked
                          ? `${title} 생성 (PLUS 이용권 기능)`
                          : `${title} 생성`
                      }
                    >
                      + {label}
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <div className="cp-apn-kind-section-body cp-apn-board-body">
              {renderKindFilter(boardKind)}
              {loading ? (
                <p className="cp-apn-empty-hint">불러오는 중…</p>
              ) : boardItems.length === 0 ? (
                <AssignmentKindEmptyState
                  message={
                    kindBaseCounts[boardKind] > 0
                      ? '조건에 맞는 과제가 없어요!'
                      : groupScopedItems.length === 0 && doc.items.length > 0
                        ? '이 그룹에 배정된 멤버가 있는 과제가 없어요!'
                        : boardKind === 'all'
                          ? '등록된 과제가 없어요!'
                          : `등록된 ${ASSIGNMENT_CREATE_KINDS.find((k) => k.kind === boardKind)?.title ?? '과제'}가 없어요!`
                  }
                />
              ) : (
                <div
                  ref={boardGridRef}
                  className="cp-apn-board-grid"
                  style={
                    {
                      '--cp-apn-board-cols': boardCols,
                    } as React.CSSProperties
                  }
                >
                  {boardVisibleItems.map((item) => (
                    <div
                      key={item.id}
                      data-apn-board-id={item.id}
                      className={`cp-apn-board-cell cp-apn-board-cell--${item.kind}`}
                    >
                      {renderAssignmentCard(item)}
                    </div>
                  ))}
                  {boardHasMore ? (
                    <div ref={boardSentinelRef} className="cp-apn-board-sentinel" aria-hidden />
                  ) : null}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {drawerItem
        ? createPortal(
            <div className="cp-apn-check-modal-root cp-apn-detail-modal-root" role="presentation">
              <div className="cp-apn-check-modal-dim" aria-hidden onClick={closeDetailModal} />
              <div className="cp-apn-check-modal-scroll" role="presentation" onClick={closeDetailModal}>
                <div
                  key={drawerItem.id}
                  className="cp-apn-detail-modal-frame"
                  role="dialog"
                  aria-modal="true"
                  aria-label={`${drawerItem.kind === 'project' ? '프로젝트' : '루틴'} 과제 상세`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="cp-apn-detail-modal-close"
                    aria-label="과제 상세 닫기"
                    onClick={closeDetailModal}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M6 6l12 12M18 6L6 18"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  <div className="cp-apn-detail-modal-panel">
                    <div
                      data-apn-board-id={drawerItem.id}
                      className={`cp-apn-board-cell cp-apn-board-cell--${drawerItem.kind} is-expanded`}
                    >
                      {renderAssignmentCard(drawerItem, true)}
                    </div>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {checkModalOpen && checkSelection
        ? createPortal(
            <div className="cp-apn-check-modal-root" role="presentation">
              <div
                className="cp-apn-check-modal-dim"
                aria-hidden
                onClick={requestCloseCheckModal}
              />
              <div
                className="cp-apn-check-modal-scroll"
                role="presentation"
                onClick={requestCloseCheckModal}
              >
                <div
                  className="cp-apn-check-modal-panel"
                  role="dialog"
                  aria-modal="true"
                  aria-label="과제 체크"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="cp-apn-check-modal-close"
                    aria-label="과제 체크 닫기"
                    onClick={(e) => {
                      e.stopPropagation();
                      requestCloseCheckModal();
                    }}
                    disabled={checkClosing}
                  >
                    <img src="/modal-close.svg" alt="" width={18} height={18} />
                  </button>
                  <AssignmentCheckPanel
                    selection={checkSelection}
                    members={members}
                    settings={doc.settings}
                    onApplyStatuses={applyCheckStatuses}
                    onDirtyChange={handleCheckPanelDirtyChange}
                    saveActionsRef={checkSaveActionsRef}
                    onChallengeReset={resetChallengeSubmittedMarks}
                  />
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {checkModalOpen && checkCloseConfirmOpen
        ? createPortal(
            <div
              className="cp-modal-backdrop cp-apn-check-close-confirm-backdrop"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cp-apn-check-close-confirm-title"
              onClick={() => {
                if (!checkClosing) setCheckCloseConfirmOpen(false);
              }}
            >
              <div className="cp-modal cp-leave-modal-box" onClick={(e) => e.stopPropagation()}>
                <h3 id="cp-apn-check-close-confirm-title" className="cp-leave-modal-title">
                  변경사항이 저장되지 않았습니다
                </h3>
                <p className="cp-leave-modal-desc">저장하지 않고 나가시겠습니까?</p>
                <div className="cp-modal-footer cp-leave-modal-actions">
                  <button
                    type="button"
                    className="cp-btn-secondary"
                    onClick={discardAndCloseCheckModal}
                    disabled={checkClosing}
                  >
                    저장하지 않고 나가기
                  </button>
                  <button
                    type="button"
                    className="cp-btn-primary"
                    onClick={() => void saveAndCloseCheckModal()}
                    disabled={checkClosing}
                  >
                    {checkClosing ? '저장 중…' : '저장 하기'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      <GeneralAssignmentSettingsModal
        open={generalModal.open}
        mode={generalModal.mode}
        initial={generalModal.item}
        members={allMembers}
        defaultGroupId={selectedGroupId}
        categories={doc.settings.categories}
        defaultPoints={doc.settings.defaultPoints}
        coverImages={coverImages}
        onCoverImagesChange={handleCoverImagesChange}
        onClose={() => setGeneralModal((s) => ({ ...s, open: false }))}
        onSave={(item) => {
          void upsertItem(item).then(() => setGeneralModal((s) => ({ ...s, open: false })));
        }}
      />
      <ProjectAssignmentSettingsModal
        open={projectModal.open}
        mode={projectModal.mode}
        initial={projectModal.item}
        members={allMembers}
        defaultGroupId={selectedGroupId}
        defaultPoints={doc.settings.defaultPoints}
        assignmentRewardItems={assignmentRewardItems}
        coverImages={coverImages}
        onCoverImagesChange={handleCoverImagesChange}
        onClose={() => setProjectModal((s) => ({ ...s, open: false }))}
        onSave={(item) => {
          void handleProjectModalSave(item);
        }}
        challengeMode={!!projectModal.challenge}
      />
      <ChallengeEditorModal
        open={!!challengeEditorItem}
        item={challengeEditorItem}
        defaultPoints={doc.settings.defaultPoints}
        coverImageUrl={challengeEditorItem ? challengeCoverUrlOf(challengeEditorItem) : ''}
        submissionCountByItemId={challengeEditorSubmissionCounts}
        onClose={() => {
          setChallengeEditorItemId(null);
          void syncChallengeSubmissions();
        }}
        onSaved={(subTasks) => handleChallengeEditorSaved(subTasks)}
      />
      <ChallengeShareModal
        open={!!challengeShareItem}
        projectId={challengeShareItem?.challenge.projectId ?? null}
        title={challengeShareItem?.title ?? ''}
        themeColor={challengeShareItem?.color}
        onClose={() => setChallengeShareItemId(null)}
      />
      <RoutineAssignmentSettingsModal
        open={routineModal.open}
        mode={routineModal.mode}
        initial={routineModal.item}
        members={allMembers}
        defaultGroupId={selectedGroupId}
        defaultPoints={doc.settings.defaultPoints}
        assignmentRewardItems={assignmentRewardItems}
        coverImages={coverImages}
        onCoverImagesChange={handleCoverImagesChange}
        onClose={() => setRoutineModal((s) => ({ ...s, open: false }))}
        onSave={(item) => {
          void upsertItem(item).then(() => setRoutineModal((s) => ({ ...s, open: false })));
        }}
      />
      <AssignmentPointsSettingsModal
        open={settingsOpen}
        settings={doc.settings}
        onClose={() => setSettingsOpen(false)}
        onSave={(settings) => {
          void applySettings(settings);
        }}
      />
      <AssignmentManagementTableModal
        open={tableModalOpen}
        doc={doc}
        members={members}
        onClose={() => setTableModalOpen(false)}
        onSave={async (next, touchedItemIds) => {
          await commitDoc(next, {
            toast: true,
            saveOpts: { syncRewardItemIds: touchedItemIds },
          });
        }}
      />
      <AssignmentIncompleteReportModal
        open={incompleteReportMode != null}
        mode={incompleteReportMode ?? 'byMember'}
        items={doc.items}
        members={members}
        onClose={() => setIncompleteReportMode(null)}
      />
      {selectedItem?.kind === 'project' ? (
        <ProjectMemberReachModal
          open={reachModalOpen}
          item={selectedItem}
          members={members}
          onClose={() => setReachModalOpen(false)}
        />
      ) : null}
      {selectedItem?.kind === 'routine' ? (
        <RoutineMemberReachModal
          open={reachModalOpen}
          item={selectedItem}
          members={members}
          onClose={() => setReachModalOpen(false)}
        />
      ) : null}
      <AssignmentRewardResultModal
        open={rewardResult != null}
        title={rewardResultTitle}
        result={rewardResult}
        onClose={() => setRewardResult(null)}
      />
      <AssignmentDateInputModal
        open={dateInputOpen}
        onClose={() => setDateInputOpen(false)}
        onConfirm={(dateIso) => {
          if (!selectedId) return;
          const now = new Date().toISOString();
          const prev = docRef.current;
          const next: AssignmentPointsNoteDoc = {
            ...prev,
            updatedAt: now,
            items: prev.items.map((item) => {
              if (item.id !== selectedId || item.kind !== 'routine') return item;
              const inSchedule = calculateRoutineDates(
                item.startDate,
                item.dueDate,
                item.routineDays
              ).includes(dateIso);
              const isCustomDate = !inSchedule;
              const idx = item.dateStatuses.findIndex((b) => b.date === dateIso);
              if (idx >= 0) {
                const dateStatuses = item.dateStatuses.map((b, i) =>
                  i === idx ? { ...b, isCustomDate: b.isCustomDate || isCustomDate } : b
                );
                return { ...item, dateStatuses, updatedAt: now };
              }
              return {
                ...item,
                dateStatuses: [
                  ...item.dateStatuses,
                  { date: dateIso, studentStatuses: [], isCustomDate },
                ],
                updatedAt: now,
              };
            }),
          };
          setExpandedId(selectedId);
          setRoutineFocusDate(dateIso);
          void commitDoc(next, { saveOpts: { syncRewardItemIds: [] } });
        }}
      />
    </div>
  );
};

export default AssignmentPointsNotePage;
